import { useCallback, useMemo } from 'react'
import {
  Alert,
  CheckboxGroup,
  ListboxGroup,
  PageSection,
  ReportingPeriodBasis
} from '@/common'
import { parsePeriodId } from '@artem-cbam-declarants/shared'
import useI18n from '@/shared/hooks/usei18n'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import {
  type GetAggregatedCategoryForCnCode,
  getNoEmissionsReportsAvailableWarningKey,
  INSTALLATION_INELIGIBILITY_STRINGS
} from '@/shared/utils/batchUpdateSelectionRules'
import { useSupplierEmissionsEligibility } from '../../../../hooks/useSupplierEmissionsEligibility'
import { EMPTY_VALUE } from '../bulkUpdateSupplierEmissionsForm/BulkUpdateSupplierEmissionsForm.schema'
import { ImportedGoodFormData } from './ImportedGoodForm.schema'

type ImportedGoodSupplierEmissionsFormProps = {
  data: ImportedGoodFormData
  onChange: (data: ImportedGoodFormData) => void
  partnerships: Partnership[]
  installations: Installation[]
  emissionsReports: SupplierEmissionsReport[]
  getAggregatedCategoryForCnCode: GetAggregatedCategoryForCnCode
  isEditMode?: boolean
  cbamReportYear?: number
}

export default function ImportedGoodSupplierEmissionsForm({
  data,
  onChange,
  partnerships,
  installations,
  emissionsReports,
  getAggregatedCategoryForCnCode,
  isEditMode = false,
  cbamReportYear
}: ImportedGoodSupplierEmissionsFormProps) {
  const { t, dateLongFormatterMonthYear } = useI18n()

  const hasSelectedInstallation = !!data.installationId && data.installationId !== EMPTY_VALUE

  const selectedGoods = useMemo(() => {
    return [
      {
        country: hasSelectedInstallation ? '' : data.country,
        cnCode: data.cnCode
      }
    ]
  }, [data.country, data.cnCode, hasSelectedInstallation])

  const { installationEligibility, emissionsEligibility } = useSupplierEmissionsEligibility({
    selectedGoods,
    installations,
    emissionsReports,
    getAggregatedCategoryForCnCode,
    selectedInstallationId: data.installationId || null,
    cbamReportYear
  })

  const selectedInstallation = useMemo(
    () =>
      installations.find(i => i.id === data.installationId && data.installationId !== EMPTY_VALUE),
    [installations, data.installationId]
  )

  const emissionReportOptions = useMemo(() => {
    const eligibleSet = new Set(emissionsEligibility.eligibleReportIds)
    let eligibleEmissionReports = emissionsReports.filter(report => eligibleSet.has(report.id))

    const linkedReportId =
      isEditMode &&
      data.supplierEmissionsReportId &&
      data.supplierEmissionsReportId !== EMPTY_VALUE &&
      !eligibleSet.has(data.supplierEmissionsReportId)
        ? data.supplierEmissionsReportId
        : null

    if (linkedReportId) {
      const legacyReport = emissionsReports.find(report => report.id === linkedReportId)
      if (legacyReport) {
        eligibleEmissionReports = [...eligibleEmissionReports, legacyReport]
      }
    }

    const formattedEmissionReports = eligibleEmissionReports.map(report => {
      const period = parsePeriodId(report.periodId)
      const periodLabel =
        selectedInstallation?.basisForReportingPeriod === ReportingPeriodBasis.CALENDAR_YEAR
          ? `${period.startDate.getFullYear()}`
          : `${dateLongFormatterMonthYear(period.startDate)} - ${dateLongFormatterMonthYear(period.endDate)}`

      return {
        value: report.id,
        label: `${report.goods[0]?.goodsDescription || 'Unknown goods'} - ${report.goods[0]?.cnCode || 'N/A'} (${periodLabel})`,
        report,
        disabled: linkedReportId === report.id
      }
    })

    const options = formattedEmissionReports.filter(
      report => eligibleSet.has(report.value) || report.value === linkedReportId
    )
    const emptyOption = isEditMode
      ? [{ value: EMPTY_VALUE, label: t('cbamReporting:bulkUpdate.clearValue'), report: undefined }]
      : []
    return [...emptyOption, ...options]
  }, [
    emissionsReports,
    selectedInstallation,
    dateLongFormatterMonthYear,
    emissionsEligibility.eligibleReportIds,
    data.supplierEmissionsReportId,
    t,
    isEditMode
  ])

  const hasNoReportsAvailable = useMemo(() => {
    if (!hasSelectedInstallation || !emissionsEligibility.canSelect || emissionsEligibility.tooltipKey) {
      return false
    }

    return emissionsEligibility.eligibleReportIds.length === 0
  }, [
    hasSelectedInstallation,
    emissionsEligibility.canSelect,
    emissionsEligibility.tooltipKey,
    emissionsEligibility.eligibleReportIds
  ])

  const noReportsAvailableWarningKey = useMemo(
    () => (hasNoReportsAvailable ? getNoEmissionsReportsAvailableWarningKey(cbamReportYear) : null),
    [hasNoReportsAvailable, cbamReportYear]
  )

  const offPlatformPartnershipIds = useMemo(
    () =>
      new Set(
        partnerships
          .filter(partnership => partnership.isOffPlatform)
          .map(partnership => partnership.id)
      ),
    [partnerships]
  )

  const supplierInstallationOptions = useMemo(() => {
    const eligibleSet = new Set(installationEligibility.eligibleInstallationIds)
    const eligibleInstallations = installations.filter(
      inst => eligibleSet.has(inst.id) && offPlatformPartnershipIds.has(inst.partnershipId)
    )
    const partnershipById = new Map(partnerships.map(partnership => [partnership.id, partnership]))
    const emptyOption = isEditMode
      ? [{ value: EMPTY_VALUE, label: t('cbamReporting:bulkUpdate.clearValue') }]
      : []
    return [
      ...emptyOption,
      ...eligibleInstallations.map(inst => {
        const partnership = partnershipById.get(inst.partnershipId)
        const label = partnership ? `${partnership.name} – ${inst.name}` : inst.name
        return { value: inst.id, label }
      })
    ]
  }, [
    installations,
    partnerships,
    offPlatformPartnershipIds,
    installationEligibility.eligibleInstallationIds,
    t,
    isEditMode
  ])

  const isSupplierInstallationDisabled = !!installationEligibility.ineligibilityReason
  const isEmissionsReportDisabled =
    data.isUsingDefaultEmissions ||
    !data.installationId ||
    data.installationId === EMPTY_VALUE ||
    !emissionsEligibility.canSelect

  const handleSupplierInstallationChange = useCallback(
    (value: string) => {
      if (value === '') {
        onChange({
          ...data,
          installationId: '',
          cnCode: '',
          description: '',
          country: '',
          supplierEmissionsReportId: ''
        })

        return
      }

      if (value === EMPTY_VALUE) {
        onChange({
          ...data,
          partnershipId: EMPTY_VALUE,
          installationId: EMPTY_VALUE,
          supplierEmissionsReportId: ''
        })
        return
      }

      const inst = installations.find(i => i.id === value)
      if (inst) {
        onChange({
          ...data,
          cnCode: '',
          partnershipId: inst.partnershipId,
          installationId: value,
          description: '',
          supplierEmissionsReportId: '',
          country: inst.country
        })
      }
    },
    [onChange, data, installations]
  )

  const handleEmissionReportChange = useCallback(
    (value: string) => {
      const selectedReport = emissionsReports.find(report => report.id === value)

      if (value === '') {
        onChange({
          ...data,
          isUsingDefaultEmissions: true,
          supplierEmissionsReportId: '',
          cnCode: '',
          description: ''
        })
        return
      }

      if (value === EMPTY_VALUE) {
        onChange({
          ...data,
          isUsingDefaultEmissions: true,
          supplierEmissionsReportId: EMPTY_VALUE
        })
        return
      }

      if (selectedReport) {
        onChange({
          ...data,
          isUsingDefaultEmissions: false,
          supplierEmissionsReportId: value,
          cnCode: selectedReport.goods[0]?.cnCode || '',
          description: selectedReport.goods[0]?.goodsDescription || ''
        })
      }

      return
    },
    [onChange, data, emissionsReports]
  )

  const handleIsUsingDefaultEmissionsChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...data,
        supplierEmissionsReportId: '',
        isUsingDefaultEmissions: checked
      })
    },
    [onChange, data]
  )

  return (
    <PageSection
      title={t('cbamReporting:cbamReportDetails.importedGoodSupplierEmissionsForm.title')}
    >
      {installationEligibility.ineligibilityReason && (
        <Alert type="warning">
          {t(INSTALLATION_INELIGIBILITY_STRINGS[installationEligibility.ineligibilityReason])}
        </Alert>
      )}
      {emissionsEligibility.tooltipKey && (
        <Alert type="warning">{t(emissionsEligibility.tooltipKey)}</Alert>
      )}
      {noReportsAvailableWarningKey && (
        <Alert type="warning">{t(noReportsAvailableWarningKey)}</Alert>
      )}
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <ListboxGroup
            label={t(
              'cbamReporting:cbamReportDetails.importedGoodSupplierEmissionsForm.fields.supplierAndInstallation.label'
            )}
            value={data.installationId || ''}
            placeholder={t(
              'cbamReporting:cbamReportDetails.importedGoodSupplierEmissionsForm.fields.supplierAndInstallation.placeholder'
            )}
            options={supplierInstallationOptions}
            onChange={handleSupplierInstallationChange}
            disabled={isSupplierInstallationDisabled}
          />
          <ListboxGroup
            label={t(
              'cbamReporting:cbamReportDetails.importedGoodSupplierEmissionsForm.fields.emissionReport.label'
            )}
            value={data.supplierEmissionsReportId || ''}
            placeholder={t(
              'cbamReporting:cbamReportDetails.importedGoodSupplierEmissionsForm.fields.emissionReport.placeholder'
            )}
            options={emissionReportOptions}
            onChange={handleEmissionReportChange}
            disabled={isEmissionsReportDisabled}
          />
          <CheckboxGroup
            id="isUsingDefaultEmissions"
            label={t(
              'cbamReporting:cbamReportDetails.importedGoodSupplierEmissionsForm.fields.isUsingDefaultEmissions'
            )}
            disabled={emissionReportOptions.length === 0}
            checked={data.isUsingDefaultEmissions}
            onChange={handleIsUsingDefaultEmissionsChange}
          />
        </div>
      </div>
    </PageSection>
  )
}
